import React, { useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { SelectContent, SelectItem } from '@/components/ui/select';

/**
 * <SelectContent> body that groups supplier products by their industrial-product
 * category and lets the user filter the list with a sticky search field.
 *
 * The search matches (case-insensitive, accent-insensitive) against the
 * supplier product name, the industrial product name and the category — so
 * the query can jump across categories without the user having to scroll.
 *
 * The caller is responsible for pre-filtering the `products` prop (e.g. by
 * supplier, "commandable" mode, "masquer déjà ajoutés" toggle) — the search
 * only narrows down what has already been made available.
 *
 * Props:
 *   - products: supplier_products already filtered by the caller
 *   - industrialProducts: full catalogue used to look up the category / name
 *   - renderLabel(sp): what to display inside each SelectItem (defaults to the
 *     supplier product name)
 *   - itemClassName: override the default compact styling
 *   - searchPlaceholder: placeholder text for the search input
 */
const stopPointer = (e) => e.preventDefault();
const normalize = (s) => (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default function CategorizedProductSelectContent({
    products,
    industrialProducts,
    getCategory,
    renderLabel,
    itemClassName,
    comfort = false,
    searchPlaceholder = 'Rechercher…',
}) {
    const resolvedItemClassName = itemClassName || (comfort
        ? 'pl-6 py-3 text-sm leading-snug'
        : 'pl-5 py-0.5 text-xs leading-tight');
    const [query, setQuery] = useState('');
    const inputRef = useRef(null);

    const ipById = useMemo(() => {
        const m = new Map();
        for (const ip of industrialProducts || []) m.set(ip.id, ip);
        return m;
    }, [industrialProducts]);

    const categoryOf = (sp) => (getCategory ? getCategory(sp) : ipById.get(sp.industrial_product_id)?.category) || 'Sans catégorie';
    const label = renderLabel || ((sp) => sp.supplier_name_product || sp.industrial_product_name);

    // Sort once (category, then name), then filter by search across all cats.
    const visibleProducts = useMemo(() => {
        const q = normalize(query.trim());
        const sorted = [...(products || [])].sort((a, b) => {
            const catA = categoryOf(a);
            const catB = categoryOf(b);
            if (catA !== catB) return catA.localeCompare(catB, 'fr');
            return (a.supplier_name_product || '').localeCompare(b.supplier_name_product || '', 'fr');
        });
        if (!q) return sorted;
        return sorted.filter((sp) => {
            const ip = ipById.get(sp.industrial_product_id);
            const haystack = normalize([
                sp.supplier_name_product,
                sp.industrial_product_name,
                ip?.name,
                categoryOf(sp),
            ].filter(Boolean).join(' '));
            return haystack.includes(q);
        });
    }, [products, ipById, query]);

    // Radix Select tries to typeahead on any keydown that bubbles up from
    // the content. We stop propagation on the search input so keystrokes go
    // to the input, not the item list. Arrow/Enter/Escape/Tab still bubble
    // (they're needed for keyboard nav & close).
    const swallowTypeahead = (e) => {
        const passthroughKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'];
        if (!passthroughKeys.includes(e.key)) e.stopPropagation();
    };

    let lastCat = null;
    return (
        <SelectContent
            onPointerMove={stopPointer}
            onPointerLeave={stopPointer}
            onCloseAutoFocus={(e) => { e.preventDefault(); }}
        >
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-1.5 py-1">
                <div className="relative">
                    <Search className={`absolute left-2 top-1/2 -translate-y-1/2 ${comfort ? 'w-4 h-4' : 'w-3 h-3'} text-slate-400 pointer-events-none`} />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={swallowTypeahead}
                        onKeyUp={swallowTypeahead}
                        placeholder={searchPlaceholder}
                        className={`w-full ${comfort ? 'h-10 pl-8 pr-8 text-sm' : 'h-7 pl-7 pr-6 text-xs'} rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white`}
                        data-testid="categorized-product-search-input"
                    />
                    {query && (
                        <button
                            type="button"
                            aria-label="Effacer la recherche"
                            onClick={(e) => { e.stopPropagation(); setQuery(''); inputRef.current?.focus(); }}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                        >
                            <X className="w-3 h-3 text-slate-400" />
                        </button>
                    )}
                </div>
            </div>
            {visibleProducts.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-slate-400">Aucun produit trouvé</div>
            ) : (
                visibleProducts.map((sp) => {
                    const cat = categoryOf(sp);
                    const showHeader = cat !== lastCat;
                    lastCat = cat;
                    return (
                        <React.Fragment key={sp.id}>
                            {showHeader && (
                                <div className={`px-2 ${comfort ? 'py-1 text-[11px]' : 'py-0 text-[10px]'} font-bold text-slate-400 uppercase tracking-wider bg-slate-50`}>
                                    {cat}
                                </div>
                            )}
                            <SelectItem
                                value={sp.id}
                                className={resolvedItemClassName}
                                onPointerMove={stopPointer}
                                onPointerLeave={stopPointer}
                            >
                                {label(sp)}
                            </SelectItem>
                        </React.Fragment>
                    );
                })
            )}
        </SelectContent>
    );
}
